<template>
  <div class="main container">
    <div class="title-header">
      <div class="row title-container">
        <div class="col-auto">
          <div class="title-header-text">{{ CONSTANTS.ENTERPRISE_SERVICE_DIRECTORY }}</div>
        </div>
        <div class="col-auto px-0">
          <employee-hub-bookmark
            :title="CONSTANTS.ENTERPRISE_SERVICE_DIRECTORY"
            show-circle
            :key="$route.fullPath"
            :env="env"
            class="d-inline-block eh-bookmark"
          ></employee-hub-bookmark>
        </div>
      </div>
    </div>

    <loading-overlay v-if="loadingServiceDirectories"></loading-overlay>
    <div class="row enterprise-services-panel">
      <div
        class="col-12 col-lg-3 service-directory-container service-directory-categories-container"
      >
        <service-directory-categories
          :selectedDirectory="selectedDirectory"
          @handleClickServiceDirectory="handleClickServiceDirectory"
        />
      </div>
      <div class="col-12 col-lg-9 services-panel">
        <template v-if="!loadingServiceDirectories">
          <enterprise-service-directory-platform
            v-for="(servicePlatform, index) in servicePlatforms"
            :key="index"
            :name="servicePlatform.name"
            :platformCategory="servicePlatform.platformCategory"
            :serviceAreas="servicePlatform.serviceAreas"
            @handleClickServiceModal="handleClickServiceModal"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { safeImportComponent } from "@/utils/componentImportUtils";
import { mapGetters, mapState, mapActions, mapMutations } from "vuex";
import LoadingOverlay from "@/components/LoadingOverlay";
import * as CONSTANTS from "@/store/constants";
import router from "@/router";
import EnterpriseServiceModal from "@/components/enterprise-service-directory/EnterpriseServiceModal";
import EnterpriseServiceDirectoryPlatform from "./EnterpriseServiceDirectoryPlatform";
import ServiceDirectoryCategories from "./ServiceDirectoryCategories";
import { createStandardModal } from "@/utils/modalHelper";

export default {
  name: "enterprise-service-directory",
  components: {
    LoadingOverlay,
    EnterpriseServiceModal,
    EnterpriseServiceDirectoryPlatform,
    ServiceDirectoryCategories
  },
  data() {
    return {
      CONSTANTS,
      selectedDirectory: "",
      modalInstance: null,
      env: CURRENT_ENV
    };
  },
  computed: {
    ...mapGetters([
      "loadingServiceDirectories",
      "serviceDirectoryTaxonomny",
      "hrTaxonomy",
      "relatedServiceDirectoryTaxonomny",
      "allServiceDirectoryTaxonomny",
      "relatedServiceAreaDirectories",
      "serviceDirectoryCategories"
    ]),
    servicePlatforms() {
      let platforms = [];
      let serviceDirectoryPlatforms = [];

      if (
        this.serviceDirectoryTaxonomny !== undefined &&
        this.serviceDirectoryTaxonomny.length != 0
      ) {
        this.serviceDirectoryTaxonomny.forEach(serviceArea => {
          let directoryServiceAreas = [];
          let directoryBusinessServiceAreas = [];
          let services = [];
          serviceArea.serviceLines.forEach(serviceLine => {
            services = serviceLine.services.sort(
              this.sortServiceHierarchyItems
            );
            directoryServiceAreas.push({
              name: serviceLine.name,
              services
            });
          });
          let serviceDirectoryPlatform = {
            name: serviceArea.name,
            serviceAreas: directoryServiceAreas,
            platformCategory: serviceArea.platformCategory  // Pass along the platform category
          };
          serviceDirectoryPlatforms.push(serviceDirectoryPlatform);
        });
      }

      let sortedServiceDirectoryPlatforms = serviceDirectoryPlatforms.sort(
        (a, b) => (a.name > b.name ? 1 : -1)
      );
      return sortedServiceDirectoryPlatforms;
    }
  },
  watch: {
    '$route.params.id'(newId) {
      // Watch for route param changes and open modal
      if (newId && this.$route.params.serviceDirectory) {
        // Don't update selectedDirectory - keep the current page context
        // Only open modal with directory from route params
        this.openServiceModal(this.$route.params.serviceDirectory, newId);
      }
    }
  },
  created() {
    this.setLoadingServiceDirectories(true);
    this.onInitialLoading();
    safeImportComponent(HH_COMPONENTS_URL + "/helphubservice.umd.min.js");
    this.selectedDirectory = this.$route.params.serviceDirectory
      ? this.$route.params.serviceDirectory
      : CONSTANTS.ALL_SERVICE_DIRECTORY;
    if (
      this.selectedDirectory &&
      this.$route.params.id &&
      this.$route.meta.isDirectRequest
    ) {
      this.openServiceDirectoryModal();
    } else {
      this.retrieveServiceDirectoryTaxonomy();
    }
  },

  methods: {
    ...mapMutations([
      "setLoadingServiceDirectories",
      "setServiceDirectoryTaxonomny",
      "setAllServiceDirectoryTaxonomny"
    ]),
    ...mapActions([
      "fetchServiceDirectoryTaxonomy",
      "fetchAllServiceDirectoriesTaxonomy",
      "fetchHRServiceDirectoryTaxonomy",
      "fetchRelatedServiceDirectoriesTaxonomy",
      "fetchRelatedServiceDirectories"
    ]),
    async retrieveServiceDirectoryTaxonomy() {
      switch (this.selectedDirectory) {
        case CONSTANTS.HR_PLATFORM:
          return this.retrieveHRDirectoryTaxonomy();
        case CONSTANTS.ALL_SERVICE_DIRECTORY:
          return await this.retrieveAllDirectoriesTaxonomy();
        default:
          return await this.retrieveDirectoryTaxonomy();
      }
    },
    async onInitialLoading() {
      if (
        this.serviceDirectoryCategories === undefined ||
        this.serviceDirectoryCategories.length == 0
      ) {
        await this.fetchRelatedServiceDirectories();
      }
    },
    async retrieveHRDirectoryTaxonomy() {
      if (this.hrTaxonomy === undefined || this.hrTaxonomy.length == 0) {
        await this.fetchHRServiceDirectoryTaxonomy().then(response => {
          this.setServiceDirectoryTaxonomny(response);
        });
        // Wait for Vue to finish rendering before hiding loader
        await this.$nextTick();
        this.setLoadingServiceDirectories(false);
      } else {
        this.setServiceDirectoryTaxonomny(this.hrTaxonomy);
        // Wait for Vue to finish rendering cached content before hiding loader
        await this.$nextTick();
        this.setLoadingServiceDirectories(false);
      }
    },
    async retrieveAllDirectoriesTaxonomy() {
      if (
        this.allServiceDirectoryTaxonomny === undefined ||
        this.allServiceDirectoryTaxonomny.length == 0
      ) {
        let allDirectoriesTaxonomny = [];
        var relatedDirectoryObject = {
          directory: CONSTANTS.SALES_DIRECTORY,
          isAllDirectoriesRequest: true
        };

        // Parallelize initial fetches: HR and Categories
        await Promise.all([
          this.fetchHRServiceDirectoryTaxonomy(),
          this.fetchRelatedServiceDirectories()
        ]);

        // Parallelize remaining fetches: Related and Rest
        const [_, restTaxonomy] = await Promise.all([
          this.fetchRelatedServiceDirectoriesTaxonomy(relatedDirectoryObject),
          this.fetchAllServiceDirectoriesTaxonomy()
        ]);

        if (this.hrTaxonomy != undefined && this.hrTaxonomy.length != 0) {
          allDirectoriesTaxonomny.push.apply(
            allDirectoriesTaxonomny,
            this.hrTaxonomy
          );
        }
        if (
          this.relatedServiceDirectoryTaxonomny != undefined &&
          this.relatedServiceDirectoryTaxonomny.length != 0
        ) {
          allDirectoriesTaxonomny.push.apply(
            allDirectoriesTaxonomny,
            this.relatedServiceDirectoryTaxonomny
          );
        }
        
        if (restTaxonomy && restTaxonomy.length > 0) {
          allDirectoriesTaxonomny.push(...restTaxonomy);
        }

        this.setServiceDirectoryTaxonomny(allDirectoriesTaxonomny);
        this.setAllServiceDirectoryTaxonomny(allDirectoriesTaxonomny);
        
        // Wait for Vue to finish rendering the new content before hiding loader
        await this.$nextTick();
        this.setLoadingServiceDirectories(false);
      } else {
        this.setServiceDirectoryTaxonomny(this.allServiceDirectoryTaxonomny);
        // Wait for Vue to finish rendering the cached content before hiding loader
        await this.$nextTick();
        this.setLoadingServiceDirectories(false);
      }
    },
    async retrieveDirectoryTaxonomy() {
      if (Object.keys(this.relatedServiceAreaDirectories).length === 0) {
        await this.fetchRelatedServiceDirectories();
      }
      if (this.selectedDirectory in this.relatedServiceAreaDirectories) {
        await this.retrieveRelatedDirectoriesTaxonomy();
      } else {
        await this.fetchServiceDirectoryTaxonomy(this.selectedDirectory).catch(() => {
          this.$router.push({ name: "page-not-found" });
        });
        // Wait for Vue to finish rendering before hiding loader
        await this.$nextTick();
        this.setLoadingServiceDirectories(false);
      }
    },
    async retrieveRelatedDirectoriesTaxonomy() {
      var serviceAreaDirectories = this.relatedServiceAreaDirectories[
        this.selectedDirectory
      ];
      if (
        serviceAreaDirectories != undefined ||
        serviceAreaDirectories.length != 0
      ) {
        var relatedDirectoryObject = {
          directory: this.selectedDirectory,
          isAllDirectoriesRequest: false
        };
        await this.fetchRelatedServiceDirectoriesTaxonomy(
          relatedDirectoryObject
        ).catch(() => {
          this.$router.push({ name: "error" });
        });
        // Wait for Vue to finish rendering before hiding loader
        await this.$nextTick();
        this.setLoadingServiceDirectories(false);
      }
    },
    async openServiceDirectoryModal() {
      // Load taxonomy first (needed for finding service's platform category in ALL view)
      await this.retrieveServiceDirectoryTaxonomy();
      // Now open the modal (will search taxonomy if directory is ALL)
      await this.openServiceModal(this.selectedDirectory, this.$route.params.id);
    },
    handleClickServiceModal(serviceObject) {
      // When viewing ALL, keep the URL as /ALL/serviceId for consistency
      // The modal will internally find the correct platform category
      // For other directories, use the actual platform category
      const directory = this.selectedDirectory === CONSTANTS.ALL_SERVICE_DIRECTORY
        ? CONSTANTS.ALL_SERVICE_DIRECTORY
        : (serviceObject.platformCategory || this.selectedDirectory);

      this.$router.push({
        name: "enterprise-service-page-modal",
        params: { serviceDirectory: directory, id: serviceObject.service.id }
      });
      this.$hhMixpanel.trackEvent("Page Hit: " + serviceObject.service.name);
    },
    handleClickServiceDirectory(directory) {
      this.$router.push({
        name: "enterprise-service-directory-page",
        params: { serviceDirectory: directory }
      });
      this.selectedDirectory = directory;
      this.$hhMixpanel.trackEvent("Directory Hit: " + this.selectedDirectory);
      this.retrieveServiceDirectoryTaxonomy();
    },
    sortServiceHierarchyItems(a, b) {
      if (a && a.name && b && b.name) {
        return a.name.localeCompare(b.name);
      }
    },
    findServicePlatformCategory(serviceId) {
      // Search through the aggregated taxonomy to find which platform category
      // this service belongs to (for handling /ALL/serviceId URLs)
      for (const serviceArea of this.serviceDirectoryTaxonomny) {
        for (const serviceLine of serviceArea.serviceLines) {
          if (serviceLine.services && serviceLine.services.some(s => s.id === serviceId)) {
            return serviceArea.platformCategory;
          }
        }
      }
      return null;  // Service not found in taxonomy
    },
    async openServiceModal(directory, serviceId) {
      // Handle legacy URLs with "ALL" directory by finding the actual platform category
      if (directory === CONSTANTS.ALL_SERVICE_DIRECTORY) {
        // Wait for taxonomy to load if not already loaded
        if (!this.serviceDirectoryTaxonomny || this.serviceDirectoryTaxonomny.length === 0) {
          // Taxonomy will be loaded by retrieveServiceDirectoryTaxonomy in created()
          // Wait a bit for it to complete
          await new Promise(resolve => setTimeout(resolve, 100));

          // Check again after waiting
          if (!this.serviceDirectoryTaxonomny || this.serviceDirectoryTaxonomny.length === 0) {
            console.warn('Taxonomy not loaded yet, cannot determine platform category');
          }
        }

        // Search for the service's actual platform category
        const actualDirectory = this.findServicePlatformCategory(serviceId);

        if (actualDirectory) {
          directory = actualDirectory;
          console.log(`[openServiceModal] Found platform category for service ${serviceId}: ${directory}`);
        } else {
          console.warn(`[openServiceModal] Could not find platform category for service ${serviceId}, will try with ALL`);
        }
      }

      // Create modal instance using vue-final-modal v4 API
      this.modalInstance = createStandardModal(EnterpriseServiceModal, {
        props: {
          directory,
          serviceId
        },
        attrs: {
          class: 'vfm-modal-center eh-service-modal'
        },
        callbacks: {
          onBeforeClose: () => {
            this.$router.push(
              `/enterprise-service-directory/${this.selectedDirectory}`
            );
          }
        },
        maxWidth: '1200px'
      });

      // Open the modal
      this.modalInstance.open();
    }
  },

  beforeUnmount() {
    // Cleanup modal instance when component is destroyed
    if (this.modalInstance) {
      this.modalInstance.destroy();
    }
  }
};
</script>

<style scoped>
.title-container {
  display: flex;
  align-content: center;
  align-items: center;
  height: auto;
}
.main {
  background-color: #ffffff !important;
  overflow: hidden;
}
.service-directory-container {
  max-width: 100%;
  padding-right: 15px;
  margin-left: 15px;
  margin-right: 15px;
}
.services-panel {
  padding: 28px 60px;
  height: auto;
  min-height: 700px;
  margin-top: 31px;
  margin-right: 1.1667%;
}
.enterprise-services-panel{
  border: 1px solid #cccccc;
  height: auto;
  min-height: 700px;
  margin-top: 31px;
  margin-bottom: 31px;
  margin-right: 0.1%;
  margin-left: 0.1%;
  border-radius: 4px;
}

.employeehub-service-container {
  max-width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  margin-left: 15px;
  margin-right: 15px;
  padding-top: 33px;
}

@media (min-width: 768px) {
  .employeehub-service-container {
    /* Approximately col-1 offset */
    margin-right: 9.1667%;
    margin-left: 19.1667%;
  }
}
@media (max-width: 767px) {
  .services-panel {
    padding: 15px 30px;
    margin: 15px 0px;
    margin-right: 9.1667%;
    margin-left: 4%;
  }
  .eh-bookmark {
    margin-top: 20px !important;
  }
  .service-directory-container {
    margin-right: 30%;
    margin-left: 30%;
  }
  .service-directory-categories-container :deep(hr) {
    border-top: 1px solid #ffffff;
  }
  .categories-options {
    text-align: center;
  }
  .title-header-text {
    font-size: 18px !important;
    padding-top: 22px !important;
  }
}

.title-header-text {
  color: #000000;
  font-size: 38px;
  font-weight: bold;
  line-height: 25px;
  font-family: artifaktElementBold;
}
.title-header {
  height: auto;
  margin: 33px 0;
}
.it-services-panel {
  margin-bottom: 50px;
}
.it-business-panel {
  margin-bottom: 20px;
}

.services-role-toggle span {
  margin: -5px 5px 0 15px;
}
.services-role-toggle span.active {
  color: #0696d7;
}

/* modal ovverrides */

.service-directory-categories-container {
  max-width: 150px;
  font-size: 16px !important;
}

.eh-service-modal {
  border-radius: 0;
}


@media (min-width: 768px) {
  .helphub-app-container {
    margin-right: 9.1667%;
    margin-left: 8%;
  }
}



@media only screen and (min-width: 1024px) and (max-height: 1366px) and (-webkit-min-device-pixel-ratio: 1.5) {
  .helphub-app-container {
    margin-left: 8% !important;
  }
  employee-hub-bookmark :deep(.bookmark-icon) {
    margin-left: 0px !important;
    margin-top: 9px !important;
  }
}

@media only screen and (min-width: 320px) and (max-height: 568px) and (-webkit-min-device-pixel-ratio: 1.5) {
  .helphub-app-container {
    margin-left: 1% !important;
  }
}

@media only screen and (min-width: 768px) and (max-height: 1024px) and (-webkit-min-device-pixel-ratio: 1.5) {
  .services-panel {
    margin-left: 4% !important;
  }
  employee-hub-bookmark :deep(.bookmark-icon) {
    margin-left: 0px !important;
    margin-top: 9px !important;
  }
}


@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
  .service-directory-container {
    margin-right: 50%;
    margin-left: 40%;
  }
  .service-directory-categories-container :deep(hr) {
    border-top: 1px solid #ffffff;
  }
}


@media only screen and (min-width: 1440px) and (max-height: 900px) and (-webkit-min-device-pixel-ratio: 1.5) {
  .helphub-app-container {
    margin-left: 4.3% !important;
  }
  employee-hub-bookmark :deep(.bookmark-icon) {
    margin-left: 4px !important;
    margin-top: 12px !important;
    font-size:28px !important;
  }
}


@media(min-width: 300px) and (max-height: 915px) {
  employee-hub-bookmark :deep(.bookmark-icon) {
    margin-left: -5px !important;
    margin-top: 11px !important;
    font-size:20px !important;
  }
}

@media (min-width: 1025px) {
employee-hub-bookmark :deep(.bookmark-icon) {
  margin-left: 4px !important;
  margin-top: 9px !important;
}
}

@media only screen and (min-width: 1366px) and (max-height: 768px),
only screen and (min-width: 1280px) and (max-height: 800px){
employee-hub-bookmark :deep(.bookmark-icon) {
  margin-left: 4px !important;
  margin-top: 11px !important;
  font-size:28px !important;
}
}
</style>
